'use client';

export type OptionType<T = unknown> = { label: string; value: T };

interface Props<T extends OptionType> {
  name: string;
  options: [T, T];
  onChange: (_changeOption: T['value']) => void;
  selectOption: T['value'];
  disabled?: boolean;
}

const RadioSwitch = <T extends OptionType>({
  name,
  options,
  selectOption,
  onChange,
  disabled = false,
}: Props<T>) => {
  const [leftOption, rightOption] = options;

  const leftInputId = `${name}_${leftOption.value}`;
  const rightInputId = `${name}_${rightOption.value}`;
  const leftOptionChecked = leftOption.value === selectOption;
  const rightOptionChecked = rightOption.value === selectOption;

  const checkedClassName = 'bg-white text-CoolGray-900';

  const onClickOption = (clickedOption: OptionType) => {
    if (selectOption === clickedOption.value) return;
    onChange(clickedOption.value);
  };

  return (
    <fieldset className="flex">
      <div
        className={`bg-CoolGray-100 rounded-[6px] p-[4px] ${
          disabled ? 'cursor-not-allowed opacity-[0.4]' : ''
        }`}
      >
        <div className="inline-flex">
          <input
            className="peer/left_switch hidden"
            type="radio"
            name={name}
            id={leftInputId}
            onClick={() => onClickOption(leftOption)}
            defaultChecked={leftOptionChecked}
            disabled={disabled}
            hidden
          />
          <label
            htmlFor={leftInputId}
            className={
              'text-sm font-bold leading-[18px] text-CoolGray-400 ' +
              'py-[8px] px-[12px] text-center cursor-pointer rounded-[4px] ' +
              (disabled ? '!cursor-not-allowed ' : '') +
              (leftOptionChecked ? checkedClassName : '')
            }
          >
            {leftOption.label}
          </label>
        </div>
        <div className="inline-flex">
          <input
            className="peer/right_switch hidden"
            type="radio"
            name={name}
            id={rightInputId}
            onClick={() => onClickOption(rightOption)}
            defaultChecked={rightOptionChecked}
            disabled={disabled}
            hidden
          />
          <label
            htmlFor={rightInputId}
            className={
              'text-sm font-bold leading-[18px] text-CoolGray-400 ' +
              'py-[8px] px-[12px] text-center cursor-pointer rounded-[4px] ' +
              (disabled ? '!cursor-not-allowed ' : '') +
              (rightOptionChecked ? checkedClassName : '')
            }
          >
            {rightOption.label}
          </label>
        </div>
      </div>
    </fieldset>
  );
};

export default RadioSwitch;
